<script lang="ts">
export interface UnstyledStepperProps {
	/**
	 * The element or component this component should render as.
	 * @defaultValue 'div'
	 */
	as?: any;
	totalSteps: number;
	modelValue?: number;
	/**
	 * The value of the step that should be active when initially rendered. Use when you do not need to control the state of the steps.
	 */
	defaultValue?: number;
	class?: any;

	/** Whether the steps must be completed in order. */
	linear?: boolean;

	/**
	 * Automatically sync the current step with the current route, allowing for user back navigation.
	 *
	 * If [linear] is `false` this will also pull the default value from the route.
	 *
	 * @default false
	 */
	syncWithRoute?: boolean;
}

export interface UnstyledStepperEmits {
	'update:modelValue': [payload: number];
	'next': [index: number];
	'prev': [index: number];
}

interface StepSlotProps {
	goToStep: (step: number) => void;
	next: () => void;
	prev: () => void;
	hasNext: boolean;
	hasPrev: boolean;
}

export interface UnstyledStepperSlots {
	default: (props: ({ step: number | undefined; direction: 'forward' | 'backward' })) => void;
	[key: `step-${number}`]: (props: StepSlotProps) => void;
}
</script>

<script setup lang="ts">
import type { RouteLocation } from 'vue-router';
import { useVModel } from '@vueuse/core';
import { Primitive } from 'reka-ui';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from '#app/composables/router';

const props = withDefaults(defineProps<UnstyledStepperProps>(), {
	defaultValue: 1,
	linear: true,
});
const emits = defineEmits<UnstyledStepperEmits>();
defineSlots<UnstyledStepperSlots>();

const route = useRoute();
const router = useRouter();

const defaultStep = computed(() => {
	const routeStep = getStepFromRoute(route);

	if (props.syncWithRoute && !props.linear && routeStep !== undefined) {
		return routeStep;
	}

	return props.defaultValue;
});

const modelValue = useVModel(props, 'modelValue', emits, {
	defaultValue: defaultStep.value,
	passive: (props.modelValue === undefined) as false,
});
const prevModelValue = ref<number>(props.modelValue ?? defaultStep.value);

watch(modelValue, (_, oldValue) => {
	prevModelValue.value = oldValue as number;
});

const direction = computed(() => (modelValue.value ?? 1) - prevModelValue.value > 0 ? 'forward' : 'backward');

router.afterEach((to) => {
	if (props.syncWithRoute) {
		const step = getStepFromRoute(to) ?? 1;
		goToStep(step, { skipSync: true });
	}
});

function goToStep(step: number, options?: { skipSync?: boolean }) {
	if (step > props.totalSteps || step < 1 || step === modelValue.value)
		return;

	if (props.linear && step > (modelValue.value ?? 1) + 1) return;

	modelValue.value = step;

	if (props.syncWithRoute && options?.skipSync !== true) {
		router.push({
			...route,
			query: {
				...route.query,
				step: step.toString(),
			},
		});
	}
}

function nextStep() {
	goToStep((modelValue.value ?? 1) + 1);
}

function prevStep() {
	goToStep((modelValue.value ?? 1) - 1);
}

function hasNext() {
	return (modelValue.value ?? 1) < props.totalSteps;
}

function hasPrev() {
	return (modelValue.value ?? 1) > 1;
}

function next() {
	if (hasNext()) {
		nextStep();
		emits('next', modelValue.value ?? 1);
	}
}

function prev() {
	if (hasPrev()) {
		prevStep();
		emits('prev', modelValue.value ?? 1);
	}
}

function getStepFromRoute(route: RouteLocation) {
	const queryStep = route.query.step as string | undefined;

	if (queryStep) {
		const parsedStep = Number.parseInt(queryStep);

		if (!Number.isNaN(parsedStep)) {
			return Math.max(0, Math.min(parsedStep, props.totalSteps));
		}
	}
}

const slotProps = computed(() => ({
	goToStep,
	next,
	prev,
	hasNext: hasNext(),
	hasPrev: hasPrev(),
}));

defineExpose({
	goToStep,
	next,
	prev,
	hasNext,
	hasPrev,
});
</script>

<template>
	<Primitive as-child>
		<slot v-bind="{ step: modelValue, direction, ...slotProps }">
			<Suspense>
				<slot :name="`step-${modelValue as number}`" v-bind="slotProps" />
			</Suspense>
		</slot>
	</Primitive>
</template>
